import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { users, ApiError } from '../lib/api';
import Layout from '../components/Layout';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; avatarUrl?: string }) => 
      users.updateProfile(data),
    onSuccess: () => {
      setSuccess('プロフィールを更新しました');
      setError('');
      refreshUser();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('更新に失敗しました');
      }
      setSuccess('');
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    updateMutation.mutate({ name, avatarUrl: avatarUrl || undefined });
  };

  // 画像リサイズ処理
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 5MB制限
    if (file.size > 5 * 1024 * 1024) {
      setError('ファイルサイズは5MB以下にしてください');
      return;
    }

    // 画像リサイズ
    const img = new Image();
    const canvas = document.createElement('canvas');
    
    img.onload = () => {
      const maxSize = 256;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      
      // Base64に変換（実際のアプリではR2にアップロード）
      const dataUrl = canvas.toDataURL('image/webp', 0.85);
      setAvatarUrl(dataUrl);
    };
    
    img.src = URL.createObjectURL(file);
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="card">
          <h1 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            プロフィール設定
          </h1>

          {success && (
            <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-600">
              {success}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* アバター */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                プロフィール画像
              </label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-4xl text-jarvis-gold">
                      {name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div>
                  <label className="btn-outline cursor-pointer inline-block">
                    画像を選択
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                  {avatarUrl && (
                    <button
                      type="button"
                      onClick={() => setAvatarUrl('')}
                      className="ml-2 text-sm text-red-500 hover:underline"
                    >
                      削除
                    </button>
                  )}
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                    256x256px以下、5MB以下
                  </p>
                </div>
              </div>
            </div>

            {/* メールアドレス（読み取り専用） */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                メールアドレス
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="input bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                メールアドレスは変更できません
              </p>
            </div>

            {/* 名前 */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                名前 *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                required
              />
            </div>

            {/* ロール（読み取り専用） */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                ロール
              </label>
              <div className="flex items-center gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm ${
                    user?.role === 'admin'
                      ? 'bg-jarvis-gold text-jarvis-navy-dark'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  style={{ color: user?.role === 'admin' ? undefined : 'var(--text-primary)' }}
                >
                  {user?.role === 'admin' ? '管理者' : user?.role === 'viewer' ? '閲覧者' : 'ユーザー'}
                </span>
                {user?.isMaster && (
                  <span className="px-3 py-1 rounded-full text-sm bg-gradient-to-r from-jarvis-gold to-jarvis-gold-light text-jarvis-navy-dark font-bold">
                    MASTER
                  </span>
                )}
              </div>
            </div>

            {/* 送信ボタン */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="btn-primary"
              >
                {updateMutation.isPending ? '保存中...' : '変更を保存'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}

